import { areObjectIdsEqual } from 'helpers/idComparison';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import type { LinkedDocumentationItem } from 'shared/domain/issue/linkedDocumentation';
import { initialLinkTargetChoice, LinkTargetChoice } from './model';

/** A choice with the documentation it was made for, so it is never applied to another one. */
interface ChoiceForDocumentation {
  documentationId: string;
  choice: LinkTargetChoice;
}

interface LinkTargetChoiceState {
  /** `undefined` while no documentation is picked. */
  choice: LinkTargetChoice | undefined;
  setChoice: (choice: LinkTargetChoice) => void;
}

/**
 * What the user links the issue to (PT-7117). Every documentation picked starts from its
 * initial choice; the user's choice stays while the same documentation is picked, also when
 * a sync replaces its object, and is forgotten when the dialog closes.
 */
export function useLinkTargetChoice(
  isOpen: boolean,
  selectedDocumentation: DocumentationModel | undefined,
  linkedDocumentation: LinkedDocumentationItem[]
): LinkTargetChoiceState {
  const [userChoice, setUserChoice] = useState<ChoiceForDocumentation | undefined>(undefined);
  const selectedDocumentationId = selectedDocumentation?._id;

  useEffect(() => {
    if (!isOpen) {
      setUserChoice(undefined);
    }
  }, [isOpen]);

  const choice = useMemo(() => {
    if (!selectedDocumentation) {
      return undefined;
    }
    return userChoice && areObjectIdsEqual(userChoice.documentationId, selectedDocumentation._id)
      ? userChoice.choice
      : initialLinkTargetChoice(selectedDocumentation, linkedDocumentation);
  }, [selectedDocumentation, linkedDocumentation, userChoice]);

  const setChoice = useCallback(
    (nextChoice: LinkTargetChoice) => {
      if (selectedDocumentationId) {
        setUserChoice({ documentationId: selectedDocumentationId, choice: nextChoice });
      }
    },
    [selectedDocumentationId]
  );

  return { choice, setChoice };
}
